<template>
  <div class="employee-roles">
    <header class="page-header">
      <button class="back" @click="$router.back()"><van-icon name="arrow-left"/></button>
      <div class="page-title">{{ isRoleFilter ? `${roleName} · 成员管理` : '成员权限' }}</div>
      <button v-if="isRoleFilter" class="add-btn" @click="openAdd">添加</button>
    </header>

    <main class="content">
      <!-- 搜索框 + tabs：只在"员工视角"展示 -->
      <section v-if="!isRoleFilter" class="search-bar">
        <van-search v-model="query" placeholder="搜索员工姓名 / 编号"
                    show-action @search="search" @clear="search"/>
      </section>

      <section v-if="!isRoleFilter" class="tabs">
        <button :class="{active: !activeScope}" @click="activeScope = ''">全部</button>
        <button :class="{active: activeScope === 'UNASSIGNED'}" @click="activeScope = 'UNASSIGNED'">未分配角色</button>
        <button :class="{active: activeScope === 'MANAGERS'}" @click="activeScope = 'MANAGERS'">主管/Boss</button>
      </section>

      <section v-if="loading" class="loading"><van-loading/></section>

      <section v-else class="emp-list">
        <div v-for="emp in filteredEmps" :key="emp.id" class="emp-card">
          <!-- 头像：有图片用 img，否则 fallback 到姓名首字 -->
          <div class="avatar-wrap">
            <img v-if="emp.headImg" :src="emp.headImg" class="avatar-img" :alt="emp.name"/>
            <div v-else class="avatar-fallback">{{ emp.name ? emp.name.slice(-1) : '?' }}</div>
          </div>
          <div class="info" @click="gotoAssign(emp)">
            <b>{{ emp.name }}</b>
            <small>{{ emp.departmentName || '—' }} · {{ emp.positionName || '—' }}</small>
            <div class="tags">
              <span v-for="r in (emp.roles || [])" :key="r.code" class="role-tag"
                    :class="{ 'role-tag-active': isRoleFilter && r.id == roleId }">
                {{ r.name }}
              </span>
              <span v-if="(!emp.roles || emp.roles.length === 0)" class="empty-tag">未分配角色</span>
            </div>
          </div>
          <!-- 角色视角：显示移除按钮；员工视角：显示箭头 -->
          <template v-if="isRoleFilter">
            <button class="remove-btn" @click.stop="removeFromRole(emp)">
              <van-icon name="cross"/> 移除
            </button>
          </template>
          <van-icon v-else name="arrow" @click="gotoAssign(emp)" class="clickable"/>
        </div>
      </section>

      <section v-else-if="!filteredEmps.length" class="empty">
        <van-empty :description="isRoleFilter ? `「${roleName}」暂无成员，点击右上角「添加」分配` : '没有找到匹配的员工'"/>
      </section>
    </main>

    <!-- 添加成员弹窗：懒加载全员列表（排除当前角色成员） -->
    <van-dialog v-model="showAddMember" title="添加成员" show-cancel-button confirm-button-text="确认添加"
                :before-close="handleDialogClose">
      <div class="add-member-panel">
        <van-search v-model="addQuery" placeholder="搜索未分配的员工" shape="round"/>
        <div v-if="addLoading" class="mini-empty">加载中…</div>
        <div v-else class="candidates">
          <div v-for="emp in addCandidates" :key="emp.id" class="cand-item"
               :class="{ picked: pickedIds.includes(emp.id) }"
               @click="togglePick(emp)">
            <div class="avatar-wrap-sm">
              <img v-if="emp.headImg" :src="emp.headImg" class="avatar-img-sm"/>
              <div v-else class="avatar-fallback-sm">{{ emp.name ? emp.name.slice(-1) : '?' }}</div>
            </div>
            <div class="info">
              <b>{{ emp.name }}</b>
              <small>{{ emp.departmentName || '—' }}</small>
            </div>
            <van-icon :name="pickedIds.includes(emp.id) ? 'success' : 'circle'" :color="pickedIds.includes(emp.id) ? '#1989fa' : '#ccc'"/>
          </div>
          <div v-if="!addCandidates.length && !addQuery.trim()" class="mini-empty">
            请输入姓名或编号搜索可添加的员工
          </div>
          <div v-else-if="!addCandidates.length" class="mini-empty">未找到匹配的员工</div>
        </div>
      </div>
    </van-dialog>
  </div>
</template>

<script>
import identityApi from '@/api/identity';

export default {
  name: 'BossEmployeeRoles',
  data() {
    return {
      loading: true,
      emps: [],              // 已带 roles + headImg 的员工列表
      query: '',
      activeScope: '',
      roleId: null,
      roleName: '',
      showAddMember: false,
      addLoading: false,
      addQuery: '',
      pickedIds: [],
      _allEmps: null,         // 懒加载的全员列表（缓存）
      _removing: false        // 防止移除连点
    };
  },
  computed: {
    isRoleFilter() { return !!this.roleId; },
    filteredEmps() {
      let list = this.emps;
      if (!this.isRoleFilter) {
        if (this.activeScope === 'UNASSIGNED') {
          list = list.filter(e => !e.roles || e.roles.length === 0);
        } else if (this.activeScope === 'MANAGERS') {
          list = list.filter(e => (e.roles || []).some(r =>
            r.code === 'TENANT_ADMIN' || r.code === 'SYSTEM_ADMIN' ||
            r.code === 'SUPERVISOR' || r.code === 'OPERATIONS_ADMIN'));
        }
        const q = (this.query || '').trim().toLowerCase();
        if (q) list = list.filter(e =>
          (e.name || '').toLowerCase().includes(q) ||
          (e.employeeNo || '').toLowerCase().includes(q));
      }
      return list;
    },
    addCandidates() {
      const q = (this.addQuery || '').trim().toLowerCase();
      if (!q) return [];
      const all = this._allEmps || [];
      const memberIds = new Set(this.emps.map(m => m.id));
      return all
        .filter(e => !memberIds.has(e.id))
        .filter(e =>
          (e.name || '').toLowerCase().includes(q) ||
          (e.employeeNo || '').toLowerCase().includes(q));
    },
  },
  async created() {
    this.roleId = this.$route.query.roleId ? Number(this.$route.query.roleId) : null;
    this.roleName = this.$route.query.roleName || '';
    await this.load();
  },
  methods: {
    isMember(emp) {
      return (emp.roles || []).some(r => r.id == this.roleId);
    },
    async load() {
      this.loading = true;
      try {
        if (this.isRoleFilter) {
          // 角色视角：只拉该角色成员
          const members = await identityApi.membersOfRole(this.roleId).catch(() => []);
          this.emps = members;
          await Promise.all([
            this._attachRolesInParallel(this.emps),
            this._attachHeadImgs(this.emps)
          ]);
        } else {
          // 员工视角：拉全员 + roles + headImg
          const list = await identityApi.listEmployees(null, this.query || null, null);
          this.emps = Array.isArray(list) ? list : list.employees || [];
          await Promise.all([
            this._attachRolesInParallel(this.emps),
            this._attachHeadImgs(this.emps)
          ]);
        }
      } catch (e) {
        this.$toast.fail(e.message || '加载员工列表失败');
      } finally {
        this.loading = false;
      }
    },
    async _attachRolesInParallel(emps) {
      if (!emps.length) return;
      const results = await Promise.all(
        emps.map(e => identityApi.rolesForEmployee(e.id).catch(() => []))
      );
      emps.forEach((e, i) => { e.roles = results[i] || []; });
    },
    /** 批量查头像 URL（后端查 us_userlogininfo.userHeadImg） */
    async _attachHeadImgs(emps) {
      if (!emps.length) return;
      try {
        const headImgMap = await identityApi.headImages();
        emps.forEach(e => { e.headImg = headImgMap[e.id] || null; });
      } catch (_) {
        // 头像接口暂时不可用时静默降级
        emps.forEach(e => { e.headImg = null; });
      }
    },
    search() { this.load(); },
    gotoAssign(emp) {
      this.$router.push(`/boss/identity/employees/${emp.id}`);
    },
    /** 懒加载全员列表（只在点击"添加"时拉一次，之后复用缓存） */
    async openAdd() {
      this.pickedIds = [];
      this.addQuery = '';
      this.showAddMember = true;
      if (this._allEmps) return;  // 已缓存
      this.addLoading = true;
      try {
        const list = await identityApi.listEmployees().catch(() => []);
        const allEmps = Array.isArray(list) ? list : (list.employees || []);
        await this._attachHeadImgs(allEmps);
        this._allEmps = allEmps;
      } catch (_) {
        this._allEmps = [];
      } finally {
        this.addLoading = false;
      }
    },
    togglePick(emp) {
      const i = this.pickedIds.indexOf(emp.id);
      if (i >= 0) this.pickedIds.splice(i, 1);
      else this.pickedIds.push(emp.id);
    },
    /** van-dialog before-close 签名：(action, done) — 取消直接关，确认才走保存 */
    handleDialogClose(action, done) {
      if (action === 'cancel') {
        this.addQuery = '';
        this.pickedIds = [];
        done();
        return;
      }
      // action === 'confirm'
      this._doConfirmAdd().then(ok => { if (ok) done(); });
    },
    async _doConfirmAdd() {
      if (!this.pickedIds.length) {
        this.$toast.fail('请选择要添加的员工');
        return false;
      }
      try {
        // 当前角色信息（用于手动构造 roles，避免异步竞态）
        const currentRole = {
          id: this.roleId,
          code: this.$route.query.roleCode || '',
          name: this.roleName
        };
        // 同步 assign（必须等后端 commit 完）
        for (const eid of this.pickedIds) {
          await identityApi.assignRole(eid, this.roleId);
        }
        // 乐观更新：手动给新员工挂上当前角色，不依赖异步查询
        for (const eid of this.pickedIds) {
          let cached = this.emps.find(x => x.id === eid);
          if (!cached) {
            cached = (this._allEmps || []).find(x => x.id === eid);
            if (cached) {
              if (!cached.roles) cached.roles = [];
              if (!cached.roles.some(r => r.id === this.roleId)) {
                cached.roles.push(currentRole);
              }
              this.emps.push(cached);
            }
          }
        }
        this.$toast.success(`已添加 ${this.pickedIds.length} 名成员`);
        return true;
      } catch (e) {
        this.$toast.fail(e.message || '添加失败');
        return false;
      }
    },
    async removeFromRole(emp) {
      if (this._removing) return;
      this._removing = true;
      const removed = this.emps.find(e => e.id === emp.id);
      // 乐观更新：先从 UI 移除，不等后端
      this.emps = this.emps.filter(e => e.id !== emp.id);
      try {
        await identityApi.unassignRole(emp.id, this.roleId);
        this.$toast.success(`已移除 ${emp.name}`);
      } catch (e) {
        // 后端失败 → 回滚
        if (removed && !this.emps.some(x => x.id === emp.id)) {
          this.emps.push(removed);
        }
        this.$toast.fail(e.message || '移除失败');
      } finally {
        this._removing = false;
      }
    }
  }
};
</script>

<style scoped>
.page-header {
  position: sticky; top: 0; z-index: 10;
  height: 44px; background: #fff;
  display: flex; align-items: center; justify-content: space-between;
  padding: 0 12px; border-bottom: 1px solid #eee;
}
.back, .add-btn { background: none; border: 0; }
.add-btn { color: #1989fa; font-weight: 500; font-size: 14px; }
.page-title { font-weight: 500; font-size: 15px; }
.content { padding: 8px 12px calc(66px + env(safe-area-inset-bottom)); }
.search-bar { background: #fff; border-radius: 10px; padding: 4px 8px; }
.tabs { display: flex; gap: 8px; padding: 10px 0; }
.tabs button {
  border: 1px solid #e4e6eb; background: #fff; color: #555;
  padding: 6px 12px; border-radius: 16px; font-size: 13px;
}
.tabs button.active { background: #e8f3ff; color: #1989fa; border-color: #1989fa; }
.emp-list { display: flex; flex-direction: column; gap: 8px; }
.emp-card {
  display: flex; align-items: center; gap: 10px;
  background: #fff; border-radius: 10px; padding: 10px 12px;
  box-shadow: 0 1px 3px rgba(0,0,0,.04);
}
/* 头像容器：有 img 用 img，没有 fallback 到字 */
.avatar-wrap, .avatar-wrap-sm {
  width: 36px; height: 36px; border-radius: 50%;
  overflow: hidden; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
}
.avatar-wrap-sm { width: 30px; height: 30px; }
.avatar-img {
  width: 100%; height: 100%; object-fit: cover;
}
.avatar-img-sm { width: 100%; height: 100%; object-fit: cover; }
.avatar-fallback {
  width: 100%; height: 100%;
  background: #1989fa; color: #fff;
  display: flex; align-items: center; justify-content: center;
  font-size: 14px;
}
.avatar-fallback-sm {
  width: 100%; height: 100%;
  background: #1989fa; color: #fff;
  display: flex; align-items: center; justify-content: center;
  font-size: 12px;
}
.info { flex: 1; min-width: 0; cursor: pointer; }
.info b { font-size: 15px; display: block; }
.info small { color: #999; font-size: 12px; }
.tags { margin-top: 4px; display: flex; flex-wrap: wrap; gap: 4px; }
.role-tag {
  font-size: 11px; padding: 1px 8px; border-radius: 8px;
  background: #e8f3ff; color: #1989fa;
}
.role-tag-active { background: #722ed1; color: #fff; }
.empty-tag { font-size: 11px; color: #aaa; }
.clickable { cursor: pointer; color: #ccc; }
.remove-btn {
  border: 1px solid #f55; background: #fff1f0; color: #f55;
  font-size: 12px; padding: 4px 10px; border-radius: 14px;
  display: flex; align-items: center; gap: 3px;
}
.loading, .empty { padding: 40px 0; text-align: center; }
/* 添加成员面板 */
.add-member-panel { max-height: 60vh; overflow: hidden; display: flex; flex-direction: column; }
.candidates { max-height: 40vh; overflow-y: auto; padding: 8px 0; }
.cand-item {
  display: flex; align-items: center; gap: 10px;
  padding: 10px 8px; border-radius: 8px;
  cursor: pointer;
}
.cand-item.picked { background: #e8f4ff; }
.cand-item .info b { font-size: 14px; }
.cand-item .info small { font-size: 11px; color: #999; }
.mini-empty { text-align: center; color: #aaa; font-size: 13px; padding: 20px; }
</style>



// WEBPACK FOOTER //
// src/views/boss/identity/EmployeeRoles.vue